/**
 * The typed HTTP client. One fetch wrapper, then one thin function per route
 * in `docs/api-contract.md` - nothing in this file invents a route, and
 * nothing in this file invents a shape.
 *
 * Every response type is imported from `@botguide/shared`. Every request body
 * type is derived from the shared Zod schema with `z.input`, which is the
 * *pre-default* shape: `createTaskSchema` defaults `lane` to `'backlog'`, so
 * a caller should be allowed to omit it. Using the shared `CreateTaskInput`
 * (a `z.infer`, i.e. post-default) would force callers to restate every
 * default at the call site.
 */
import type {
  ApiError,
  ApplicationDto,
  BoardDto,
  BoardQuery,
  CapacityQuery,
  CapacityReport,
  ClientDashboardDto,
  ClientDto,
  FeatureDto,
  ProjectDto,
  SessionDto,
  TaskDto,
  TimeEntryDto,
  TimesheetQuery,
  UserDto,
  createApplicationSchema,
  createClientSchema,
  setUpClientSchema,
  createFeatureSchema,
  createProjectSchema,
  createTaskSchema,
  createTimeEntrySchema,
  createPayRateSchema,
  createUserSchema,
  moveTaskSchema,
  requestCodeSchema,
  reviewTimeEntrySchema,
  updateApplicationSchema,
  updateClientSchema,
  updateFeatureSchema,
  updateProjectSchema,
  updateTaskSchema,
  updateTimeEntrySchema,
  updateUserSchema,
  verifyCodeSchema,
} from '@botguide/shared';
import type { z } from 'zod';

/** The body a route accepts, before Zod applies its defaults. */
type Body<S extends z.ZodTypeAny> = z.input<S>;

export const API_BASE = '/api';

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Every non-2xx becomes one of these. Screens branch on `status` or on
 * `error`, never on a substring of `message` - `message` is prose for humans
 * and is allowed to change.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  /** The machine-readable code: `invalid_request`, `forbidden`, ... */
  readonly error: string;
  readonly details?: unknown;

  constructor(status: number, body: ApiError) {
    super(body.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.error = body.error;
    this.details = body.details;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidation(): boolean {
    return this.status === 400;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/**
 * A network failure is not an HTTP status, and a screen that says
 * "Forbidden" when the wifi dropped is lying. Give it its own shape.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/* -------------------------------------------------------------------------- */
/* The session-expiry hook                                                    */
/* -------------------------------------------------------------------------- */

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * `SessionProvider` registers the "your session is gone" behaviour here rather
 * than this module importing the router: the client must not depend on React.
 * Returns an unsubscribe so React 19's double-invoked effects cannot leave a
 * stale handler behind.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  onUnauthorized = handler;
  return () => {
    if (onUnauthorized === handler) onUnauthorized = null;
  };
}

/* -------------------------------------------------------------------------- */
/* apiFetch                                                                   */
/* -------------------------------------------------------------------------- */

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON. Pass nothing for a bodyless request. */
  json?: unknown;
  /**
   * `'redirect'` (default) hands a 401 to the session handler. `'ignore'` is
   * for the two calls that legitimately expect one - probing the session on
   * boot, and signing out - where redirecting would either loop or fight the
   * caller's own navigation.
   */
  on401?: 'redirect' | 'ignore';
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'error' in body && 'message' in body) {
      return body as ApiError;
    }
  } catch {
    // A proxy, a gateway or a crash can return HTML. Fall through.
  }
  return {
    error: `http_${response.status}`,
    message: response.statusText || `The server returned ${response.status}.`,
  };
}

export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const { json, on401 = 'redirect', headers, ...rest } = init;

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');
  if (json !== undefined) requestHeaders.set('Content-Type', 'application/json');

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      headers: requestHeaders,
      // The session is an HttpOnly cookie. Without this it is simply not sent.
      credentials: 'include',
      body: json === undefined ? undefined : JSON.stringify(json),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    const body = await readError(response);
    if (response.status === 401 && on401 === 'redirect') onUnauthorized?.();
    throw new ApiRequestError(response.status, body);
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * `{ a: 1, b: undefined }` -> `'?a=1'`. Empty query yields an empty string.
 *
 * Takes `object` rather than a `Record` so the shared query types - which are
 * `z.infer` aliases, not index-signature types - can be passed straight in.
 */
function query(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

export const authApi = {
  /**
   * Always resolves for a well-formed address, whether or not an account
   * exists - see the contract. The UI must not infer anything from success.
   */
  /*
   * The contract promises `{ ok: true }`; the API also tells us how long the
   * code lasts. Optional here so the client keeps working if that is ever
   * dropped - the sign-in screen falls back to not naming a number.
   */
  requestCode: (body: Body<typeof requestCodeSchema>) =>
    apiFetch<{ ok: true; expiresInMinutes?: number }>('/auth/request-code', {
      method: 'POST',
      json: body,
      on401: 'ignore',
    }),

  verify: (body: Body<typeof verifyCodeSchema>) =>
    apiFetch<SessionDto>('/auth/verify', { method: 'POST', json: body, on401: 'ignore' }),

  signOut: () => apiFetch<void>('/auth/sign-out', { method: 'POST', on401: 'ignore' }),

  session: () => apiFetch<SessionDto>('/auth/session', { on401: 'ignore' }),
};

/* -------------------------------------------------------------------------- */
/* Work hierarchy                                                             */
/* -------------------------------------------------------------------------- */

export const clientsApi = {
  list: () => apiFetch<ClientDto[]>('/clients'),
  get: (id: string) => apiFetch<ClientDto>(`/clients/${id}`),
  create: (body: Body<typeof createClientSchema>) =>
    apiFetch<ClientDto>('/clients', { method: 'POST', json: body }),
  /**
   * Client, application, project and feature in one call - `docs/FINDINGS.md`
   * F8. One transaction server-side, so a failure part-way leaves nothing
   * rather than a client with no application.
   */
  setUp: (body: Body<typeof setUpClientSchema>) =>
    apiFetch<{
      client: ClientDto;
      application: ApplicationDto;
      project: ProjectDto;
      feature: FeatureDto;
    }>('/clients/setup', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateClientSchema>) =>
    apiFetch<ClientDto>(`/clients/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/clients/${id}`, { method: 'DELETE' }),
};

export const applicationsApi = {
  list: (params: { clientId?: string } = {}) =>
    apiFetch<ApplicationDto[]>(`/applications${query(params)}`),
  get: (id: string) => apiFetch<ApplicationDto>(`/applications/${id}`),
  create: (body: Body<typeof createApplicationSchema>) =>
    apiFetch<ApplicationDto>('/applications', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateApplicationSchema>) =>
    apiFetch<ApplicationDto>(`/applications/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/applications/${id}`, { method: 'DELETE' }),
};

export const projectsApi = {
  list: (params: { clientId?: string; applicationId?: string } = {}) =>
    apiFetch<ProjectDto[]>(`/projects${query(params)}`),
  get: (id: string) => apiFetch<ProjectDto>(`/projects/${id}`),
  create: (body: Body<typeof createProjectSchema>) =>
    apiFetch<ProjectDto>('/projects', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateProjectSchema>) =>
    apiFetch<ProjectDto>(`/projects/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/projects/${id}`, { method: 'DELETE' }),
};

export const featuresApi = {
  list: (params: { projectId?: string; clientId?: string } = {}) =>
    apiFetch<FeatureDto[]>(`/features${query(params)}`),
  get: (id: string) => apiFetch<FeatureDto>(`/features/${id}`),
  create: (body: Body<typeof createFeatureSchema>) =>
    apiFetch<FeatureDto>('/features', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateFeatureSchema>) =>
    apiFetch<FeatureDto>(`/features/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/features/${id}`, { method: 'DELETE' }),
};

export const tasksApi = {
  list: (params: BoardQuery = {}) => apiFetch<TaskDto[]>(`/tasks${query(params)}`),
  get: (id: string) => apiFetch<TaskDto>(`/tasks/${id}`),
  create: (body: Body<typeof createTaskSchema>) =>
    apiFetch<TaskDto>('/tasks', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateTaskSchema>) =>
    apiFetch<TaskDto>(`/tasks/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/tasks/${id}`, { method: 'DELETE' }),
  /** Lane, stage and position in one atomic call - see `moveTaskSchema`. */
  move: (id: string, body: Body<typeof moveTaskSchema>) =>
    apiFetch<TaskDto>(`/tasks/${id}/move`, { method: 'POST', json: body }),
};

export const boardApi = {
  get: (params: BoardQuery = {}) => apiFetch<BoardDto>(`/board${query(params)}`),
};

/* -------------------------------------------------------------------------- */
/* People                                                                     */
/* -------------------------------------------------------------------------- */

export const usersApi = {
  /** Already passed through `redactUser` - pay fields may simply be absent. */
  list: () => apiFetch<UserDto[]>('/users'),
  me: () => apiFetch<UserDto>('/users/me'),
  create: (body: Body<typeof createUserSchema>) =>
    apiFetch<UserDto>('/users', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateUserSchema>) =>
    apiFetch<UserDto>(`/users/${id}`, { method: 'PATCH', json: body }),

  /**
   * A person's pay history, newest first.
   *
   * Append-only, per ADR-0006: there is no update and no delete, because a
   * rate edited in place silently re-prices work that may already have been
   * invoiced. A correction is a new row on a new date.
   */
  payRates: (id: string) => apiFetch<PayRateDto[]>(`/users/${id}/pay-rates`),
  addPayRate: (id: string, body: Omit<Body<typeof createPayRateSchema>, 'userId'>) =>
    apiFetch<PayRateDto>(`/users/${id}/pay-rates`, { method: 'POST', json: body }),
};

export interface PayRateDto {
  id: string;
  userId: string;
  hourlyRateCents: number;
  effectiveFrom: string;
}

/* -------------------------------------------------------------------------- */
/* Hours                                                                      */
/* -------------------------------------------------------------------------- */

export const timeEntriesApi = {
  list: (params: TimesheetQuery = {}) => apiFetch<TimeEntryDto[]>(`/time-entries${query(params)}`),
  create: (body: Body<typeof createTimeEntrySchema>) =>
    apiFetch<TimeEntryDto>('/time-entries', { method: 'POST', json: body }),
  update: (id: string, body: Body<typeof updateTimeEntrySchema>) =>
    apiFetch<TimeEntryDto>(`/time-entries/${id}`, { method: 'PATCH', json: body }),
  remove: (id: string) => apiFetch<void>(`/time-entries/${id}`, { method: 'DELETE' }),
  review: (id: string, body: Body<typeof reviewTimeEntrySchema>) =>
    apiFetch<TimeEntryDto>(`/time-entries/${id}/review`, { method: 'POST', json: body }),
};

/* -------------------------------------------------------------------------- */
/* Planning and reporting                                                     */
/* -------------------------------------------------------------------------- */

export const capacityApi = {
  get: (params: Partial<CapacityQuery> = {}) => apiFetch<CapacityReport>(`/capacity${query(params)}`),
};

export const dashboardApi = {
  /** The caller's own client. */
  mine: () => apiFetch<ClientDashboardDto>('/client-dashboard'),
  /** An owner previewing one client's view. 404 for anyone else. */
  forClient: (clientId: string) => apiFetch<ClientDashboardDto>(`/client-dashboard/${clientId}`),
};
